import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, Tooltip, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { type ReactNode } from 'react';
import { get, post, del } from '../services/httpClient';
import { isApiResponseError } from './DynamicForm/DynamicFormTypes';
import Button from 'antd/lib/button';
import { hasPermission } from '../services/permissionService';
import { getPermissionKeyFromApi } from './fromatPermission';
import { t } from 'i18next';

const { confirm } = Modal;

interface ConfirmationProps {
    apiUrl: string;
    id?: number | string | unknown;
    title: string;
    method?: 'get' | 'post' | 'delete';
    queryKey?: string | unknown[];
    queryKeySecond?: string | unknown[];
    icon?: ReactNode;
    payloadKey?: string;
    successMessage?: string; // ✅ New prop
    confirmButtonText?: string;
    isPostById?: boolean;
    extraPayload?: Record<string, any>;

}

const Confirmation: React.FC<ConfirmationProps> = ({
    apiUrl,
    id,
    title,
    method = 'post',
    queryKey,
    queryKeySecond,
    icon = <DeleteOutlined />,
    payloadKey = 'id',
    successMessage,
    confirmButtonText,
    isPostById = false,
    extraPayload = {}, // ✅ Default to empty object
}) => {
    const queryClient = useQueryClient();
    // ✅ Create base payload and merge with extraPayload (if object)
    const basePayload = isPostById ? id : { [payloadKey]: id };
    const finalPayload =
        typeof basePayload === 'object'
            ? { ...basePayload, ...extraPayload }
            : basePayload;



    const mutation = useMutation({
        mutationFn: async () => {
            if (method === 'get') {
                return await get<boolean>(apiUrl);
            } else if (method === 'post') {
                return await post<boolean>(apiUrl, finalPayload);
            } else if (method === 'delete') {
                const actualDeleteUrl = apiUrl ? `${apiUrl}?id=${id}` : ``;
                return await del<boolean>(actualDeleteUrl);
            }
        },
        onSuccess: (data: unknown) => {
            if (isApiResponseError(data)) {
                message.error(data.message || 'An error occurred');
                return;
            }

            if (queryKey) {
                // invalidate by prefix so keys like
                // [KEY, id, cardNumber, nationalID] are matched
                queryClient.invalidateQueries({
                    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
                    exact: false,
                });
            }

            if (queryKeySecond) {
                // invalidate by prefix so keys like
                // [KEY, id, cardNumber, nationalID] are matched
                queryClient.invalidateQueries({
                    queryKey: Array.isArray(queryKeySecond) ? queryKeySecond : [queryKeySecond],
                    exact: false,
                });
            }

            // fallback: refetch any matching queries to guarantee fresh data
            if (queryKey) {
                queryClient.refetchQueries({
                    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
                    exact: false,
                });
            }

            if (successMessage) {
                message.success(successMessage);
            }
        },
    });

    //const showDeleteConfirm = () => {
    //  confirm({
    //    title: confirmButtonText ??
    //      `Are you sure you want to delete this ${title}?`, 
    //    icon: <ExclamationCircleOutlined />,
    //    okText: 'Yes',
    //    okType: 'danger',
    //    cancelText: 'No',
    //    onOk: mutation.mutateAsync,
    //  });
    //  };

    const showConfirm = () => {
        confirm({
            title:
                confirmButtonText ??
                t('Are you sure you want to ') + `${title}?`,
            icon: <ExclamationCircleOutlined />,
            okText: t('Yes'),
            okType: title.toLowerCase().includes('delete') ? 'danger' : 'primary',
            cancelText: t('No'),
            onOk: mutation.mutateAsync,
        });
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        //showDeleteConfirm();
        showConfirm();
    };

    return (
        <>
            {
                hasPermission(getPermissionKeyFromApi(apiUrl)) && (
                    <Tooltip title={title}>
                        <Button size={'large'}
                            style={{
                                height: '32px',
                                width: '32px',
                                cursor: 'pointer', color: title.toLowerCase().includes('delete') ? 'red' : 'green'
                            }}
                            icon={icon}
                            onClick={handleClick}
                        />
                    </Tooltip>
                )
            }
        </>
    )
};

//<Typography.Link onClick={handleClick} >
//    {t(title)}
//</Typography.Link>


export default Confirmation;
